import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Phone,
  Envelope,
  Calendar,
  ChatCircle,
  Plus,
  Upload,
  Download,
  Share,
  Clock,
  Link as LinkIcon,
  Pencil,
  Trash,
  Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DocumentViewer } from '../components/DocumentViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecordDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [structure, setStructure] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isInteractionDialogOpen, setIsInteractionDialogOpen] = useState(false);
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isEditEmailDialogOpen, setIsEditEmailDialogOpen] = useState(false);
  const [isShareDocDialogOpen, setIsShareDocDialogOpen] = useState(false);
  
  // Form data
  const [interactionForm, setInteractionForm] = useState({ type: 'call', notes: '', status_change: '' });
  const [reminderForm, setReminderForm] = useState({ title: '', description: '', due_date: '' });
  const [linkForm, setLinkForm] = useState({ target_branch_id: '', expires_hours: 24, allowed_ips: '', is_external: false });
  const [generatedLinkUrl, setGeneratedLinkUrl] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [shareDocId, setShareDocId] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  
  // Editable fields state
  const [isEditingFields, setIsEditingFields] = useState(false);
  const [editedData, setEditedData] = useState({});

  const fetchRecord = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/records/${id}`);
      setRecord(data);
      
      if (data.structure_id) {
        const structRes = await axios.get(`${API_URL}/api/data-structures/${data.structure_id}`);
        setStructure(structRes.data);
      }
    } catch (e) {
      toast.error('Failed to fetch record');
      navigate('/records');
    }
  }, [id, navigate]);

  const fetchInteractions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/interactions/${id}`);
      setInteractions(data);
    } catch (e) {
      console.error('Failed to fetch interactions');
    }
  }, [id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/documents/${id}`);
      setDocuments(data);
    } catch (e) {
      console.error('Failed to fetch documents');
    }
  }, [id]);

  const fetchReminders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reminders?record_id=${id}`);
      setReminders(data);
    } catch (e) {
      console.error('Failed to fetch reminders');
    }
  }, [id]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statusesRes, branchesRes] = await Promise.all([
          axios.get(`${API_URL}/api/statuses`),
          axios.get(`${API_URL}/api/branches`)
        ]);
        setStatuses(statusesRes.data);
        setBranches(branchesRes.data);
      } catch (e) {
        console.error('Failed to fetch metadata');
      }
      
      await Promise.all([fetchRecord(), fetchInteractions(), fetchDocuments(), fetchReminders()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchRecord, fetchInteractions, fetchDocuments, fetchReminders]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/records/${id}`, { status: newStatus });
      toast.success('Status updated');
      fetchRecord();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/interactions`, {
        record_id: id,
        ...interactionForm
      });
      toast.success('Interaction added');
      setIsInteractionDialogOpen(false);
      setInteractionForm({ type: 'call', notes: '', status_change: '' });
      fetchInteractions();
      if (interactionForm.status_change) fetchRecord();
    } catch (e) {
      toast.error('Failed to add interaction');
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/reminders`, {
        record_id: id,
        ...reminderForm
      });
      toast.success('Reminder created');
      setIsReminderDialogOpen(false);
      setReminderForm({ title: '', description: '', due_date: '' });
      fetchReminders();
    } catch (e) {
      toast.error('Failed to create reminder');
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await axios.put(`${API_URL}/api/reminders/${reminderId}`, { is_completed: true });
      toast.success('Reminder completed');
      fetchReminders();
    } catch (e) {
      toast.error('Failed to update reminder');
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('record_id', id);

    try {
      await axios.post(`${API_URL}/api/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      fetchDocuments();
    } catch (e) {
      toast.error('Failed to upload document');
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/download/${docId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Failed to download document');
    }
  };

  const handleShareDocument = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/documents/${shareDocId}/share`, { email: shareEmail });
      toast.success('Document shared');
      setIsShareDocDialogOpen(false);
      setShareDocId('');
      setShareEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to share document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (e) {
      toast.error('Failed to delete document');
    }
  };

  const handleCreateTemporaryLink = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        record_id: id,
        target_branch_id: linkForm.is_external ? null : linkForm.target_branch_id,
        is_external: linkForm.is_external,
        expires_hours: parseInt(linkForm.expires_hours),
        allowed_ips: linkForm.allowed_ips ? linkForm.allowed_ips.split(',').map(ip => ip.trim()) : []
      };
      const { data } = await axios.post(`${API_URL}/api/temporary-links`, payload);
      toast.success('Temporary link created');
      setGeneratedLinkUrl(data.link_url);
    } catch (e) {
      toast.error('Failed to create link');
    }
  };

  const [copyFeedback, setCopyFeedback] = useState('');
  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(text);
      setCopyFeedback('Copied!');
      toast.success('Copied to clipboard');
    } catch {
      // Fallback: try execCommand
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          setCopyFeedback('Copied!');
          toast.success('Copied to clipboard');
        } else {
          setCopyFeedback('Select the URL and press Ctrl+C');
        }
      } catch {
        setCopyFeedback('Select the URL and press Ctrl+C');
      }
    }
    setTimeout(() => setCopyFeedback(''), 3000);
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/records/${id}/email`, { email: newEmail });
      toast.success('Email updated (audit logged)');
      setIsEditEmailDialogOpen(false);
      setNewEmail('');
      fetchRecord();
    } catch (e) {
      toast.error('Failed to update email');
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={16} />;
      case 'email': return <Envelope size={16} />;
      case 'meeting': return <Calendar size={16} />;
      default: return <ChatCircle size={16} />;
    }
  };

  const getStatusColor = (statusName) => {
    const status = statuses.find(s => s.name === statusName);
    return status?.color || '#666';
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const allFields = (structure?.fields || []).filter(f => f.visible !== false);
  // CSV-origin fields (original schema fields from the spreadsheet)
  const csvFields = allFields.filter(f => f.source !== 'manual');
  // Manually-added fields (added later by admin, not part of CSV)
  const manualFields = allFields.filter(f => f.source === 'manual');

  // For CSV fields: editable if field.editable=true OR user is super_admin
  const canEditCsvField = (field) => isSuperAdmin || field.editable;
  // For manual fields: show in "Additional Fields" section, editable if field.editable=true OR user is super_admin
  const canEditManualField = (field) => isSuperAdmin || field.editable;

  // State for inline editing individual CSV fields
  const [inlineEditField, setInlineEditField] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState('');

  const handleInlineEdit = (field) => {
    setInlineEditField(field.name);
    setInlineEditValue(record.data?.[field.name] ?? '');
  };

  const handleInlineSave = async (fieldName) => {
    try {
      const merged = { ...record.data, [fieldName]: inlineEditValue };
      await axios.put(`${API_URL}/api/records/${id}`, { data: merged });
      toast.success(`${fieldName} updated`);
      setInlineEditField(null);
      fetchRecord();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleInlineCancel = () => {
    setInlineEditField(null);
    setInlineEditValue('');
  };

  // Editable additional fields (manual fields section)
  const editableManualFields = manualFields.filter(f => canEditManualField(f));

  const handleStartEditFields = () => {
    const currentData = {};
    editableManualFields.forEach(f => {
      currentData[f.name] = record.data?.[f.name] ?? '';
    });
    setEditedData(currentData);
    setIsEditingFields(true);
  };

  const handleSaveFields = async () => {
    try {
      const merged = { ...record.data, ...editedData };
      await axios.put(`${API_URL}/api/records/${id}`, { data: merged });
      toast.success('Fields updated');
      setIsEditingFields(false);
      fetchRecord();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update fields');
    }
  };

  // Branch override for super_admin
  const handleBranchChange = async (newBranchId) => {
    try {
      await axios.put(`${API_URL}/api/records/${id}`, { branch_id: newBranchId });
      toast.success('Branch allocation updated');
      fetchRecord();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update branch');
    }
  };

  const renderFieldInput = (field, value, onChange) => {
    switch (field.type) {
      case 'date':
        return <Input type="date" value={value} onChange={e => onChange(e.target.value)} data-testid={`edit-field-${field.name}`} />;
      case 'number':
      case 'decimal':
        return <Input type="number" value={value} onChange={e => onChange(e.target.value)} data-testid={`edit-field-${field.name}`} />;
      case 'email':
        return <Input type="email" value={value} onChange={e => onChange(e.target.value)} data-testid={`edit-field-${field.name}`} />;
      case 'phone':
        return <Input type="tel" value={value} onChange={e => onChange(e.target.value)} data-testid={`edit-field-${field.name}`} />;
      case 'comments':
        return <Textarea value={value} onChange={e => onChange(e.target.value)} rows={3} data-testid={`edit-field-${field.name}`} />;
      case 'radio':
        return (
          <select
            value={value}
            onChange={e => onChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid={`edit-field-${field.name}`}
          >
            <option value="">Select...</option>
            {(field.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'multi_select': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-1.5" data-testid={`edit-field-${field.name}`}>
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selected.includes(opt)}
                  onCheckedChange={(checked) => {
                    const updated = checked ? [...selected, opt] : selected.filter(v => v !== opt);
                    onChange(updated);
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      }
      default:
        return <Input value={value} onChange={e => onChange(e.target.value)} data-testid={`edit-field-${field.name}`} />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 skeleton"></div>
        <div className="h-96 skeleton rounded-md"></div>
      </div>
    );
  }

  if (!record) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/records')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {record.data?.name || record.data?.customer_name || `Record ${record.id.slice(0, 8)}`}
            </h1>
            <p className="text-muted-foreground text-sm">
              {structure?.name} • Created {new Date(record.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={record.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(s => (
                <SelectItem key={s.id} value={s.name}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setIsLinkDialogOpen(true)} data-testid="create-temp-link-btn">
            <LinkIcon size={18} className="mr-2" />
            Temp Link
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Record Data - CSV Fields */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Record Details</CardTitle>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">Branch:</Label>
                    <select
                      value={record.branch_id || ''}
                      onChange={(e) => handleBranchChange(e.target.value)}
                      className="h-8 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="branch-override-select"
                    >
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {csvFields.map(field => {
                  const isEditing = inlineEditField === field.name;
                  const editable = canEditCsvField(field);
                  return (
                    <div key={field.name} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {field.name}
                        {field.required && <span className="text-destructive ml-0.5">*</span>}
                      </Label>
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1">
                            {renderFieldInput(field, inlineEditValue, setInlineEditValue)}
                          </div>
                          <Button size="icon" className="h-8 w-8 shrink-0" onClick={() => handleInlineSave(field.name)} data-testid={`save-inline-${field.name}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleInlineCancel} data-testid={`cancel-inline-${field.name}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group">
                          <p className="text-sm font-medium flex-1">
                            {Array.isArray(record.data?.[field.name]) ? record.data[field.name].join(', ') : (record.data?.[field.name] || '-')}
                          </p>
                          {editable && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                              onClick={() => handleInlineEdit(field)}
                              data-testid={`inline-edit-${field.name}`}
                            >
                              <Pencil size={12} />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {csvFields.length === 0 && Object.entries(record.data || {}).map(([key, value]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{key}</Label>
                    <p className="text-sm font-medium">{String(value) || '-'}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Fields (Manually added, not from CSV) */}
          {manualFields.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg">Additional Fields</CardTitle>
                  <CardDescription className="text-xs">Custom fields added to this schema</CardDescription>
                </div>
                {editableManualFields.length > 0 && (
                  !isEditingFields ? (
                    <Button size="sm" variant="outline" onClick={handleStartEditFields} data-testid="edit-fields-btn">
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setIsEditingFields(false)} data-testid="cancel-edit-fields-btn">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveFields} data-testid="save-fields-btn">
                        Save Changes
                      </Button>
                    </div>
                  )
                )}
              </CardHeader>
              <CardContent>
                {isEditingFields ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {editableManualFields.map(field => (
                      <div key={field.name} className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">
                          {field.name}
                          {field.required && <span className="text-destructive ml-0.5">*</span>}
                        </Label>
                        {renderFieldInput(field, editedData[field.name] ?? '', (v) => setEditedData(prev => ({ ...prev, [field.name]: v })))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {manualFields.map(field => (
                      <div key={field.name} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          {field.name}
                          {field.required && <span className="text-destructive ml-0.5">*</span>}
                          {canEditManualField(field) && <Pencil size={10} className="inline ml-1 text-primary opacity-60" />}
                        </Label>
                        <p className="text-sm font-medium">{Array.isArray(record.data?.[field.name]) ? record.data[field.name].join(', ') : (record.data?.[field.name] || '-')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs defaultValue="interactions">
            <TabsList>
              <TabsTrigger value="interactions" data-testid="interactions-tab">
                Interactions ({interactions.length})
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="documents-tab">
                Documents ({documents.length})
              </TabsTrigger>
              <TabsTrigger value="reminders" data-testid="reminders-tab">
                Reminders ({reminders.length})
              </TabsTrigger>
            </TabsList>

            {/* Interactions Tab */}
            <TabsContent value="interactions" className="mt-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">Activity Timeline</CardTitle>
                  <Button size="sm" onClick={() => setIsInteractionDialogOpen(true)} data-testid="add-interaction-btn">
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </CardHeader>
                <CardContent>
                  {interactions.length > 0 ? (
                    <div className="space-y-0">
                      {interactions.map((interaction, idx) => (
                        <div key={interaction.id} className="timeline-item">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                              {getInteractionIcon(interaction.type)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs capitalize">{interaction.type}</Badge>
                                {interaction.status_change && (
                                  <Badge variant="secondary" className="text-xs">
                                    Status → {interaction.status_change}
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground ml-auto">
                                  {new Date(interaction.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm">{interaction.notes}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                by {interaction.created_by_name}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state py-8">
                      <ChatCircle size={32} className="mb-2 opacity-50" />
                      <p>No interactions yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">Documents</CardTitle>
                  <Label htmlFor="doc-upload" className="cursor-pointer">
                    <Button size="sm" asChild>
                      <span><Upload size={16} className="mr-1" /> Upload</span>
                    </Button>
                    <Input
                      id="doc-upload"
                      type="file"
                      className="hidden"
                      onChange={handleUploadDocument}
                      data-testid="doc-upload-input"
                    />
                  </Label>
                </CardHeader>
                <CardContent>
                  {documents.length > 0 ? (
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-secondary/50 rounded-md">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setViewingDoc(doc)}
                              data-testid={`view-doc-${doc.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setShareDocId(doc.id); setIsShareDocDialogOpen(true); }}
                              data-testid={`share-doc-${doc.id}`}
                            >
                              <Share size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-destructive"
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state py-8">
                      <Upload size={32} className="mb-2 opacity-50" />
                      <p>No documents uploaded</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reminders Tab */}
            <TabsContent value="reminders" className="mt-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base">Reminders</CardTitle>
                  <Button size="sm" onClick={() => setIsReminderDialogOpen(true)} data-testid="add-reminder-btn">
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </CardHeader>
                <CardContent>
                  {reminders.length > 0 ? (
                    <div className="space-y-2">
                      {reminders.map(reminder => (
                        <div 
                          key={reminder.id} 
                          className={`flex items-start gap-3 p-3 rounded-md ${reminder.is_completed ? 'bg-secondary/30 opacity-60' : 'bg-secondary/50'}`}
                        >
                          <Clock size={18} className="text-primary mt-0.5" />
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${reminder.is_completed ? 'line-through' : ''}`}>
                              {reminder.title}
                            </p>
                            {reminder.description && (
                              <p className="text-xs text-muted-foreground">{reminder.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Due: {new Date(reminder.due_date).toLocaleDateString()}
                            </p>
                          </div>
                          {!reminder.is_completed && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCompleteReminder(reminder.id)}
                              data-testid={`complete-reminder-${reminder.id}`}
                            >
                              Complete
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state py-8">
                      <Clock size={32} className="mb-2 opacity-50" />
                      <p>No reminders set</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor(record.status) }} />
                  <span className="text-sm font-medium">{record.status}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Branch</Label>
                <p className="text-sm font-medium mt-1">
                  {branches.find(b => b.id === record.branch_id)?.name || '-'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Record ID</Label>
                <p className="text-sm font-mono mt-1">{record.id}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Last Updated</Label>
                <p className="text-sm mt-1">{new Date(record.updated_at).toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Interaction Dialog */}
      <Dialog open={isInteractionDialogOpen} onOpenChange={setIsInteractionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Interaction</DialogTitle>
            <DialogDescription>Log a new interaction with this record</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddInteraction} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={interactionForm.type} onValueChange={(v) => setInteractionForm({...interactionForm, type: v})}>
                  <SelectTrigger data-testid="interaction-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Change Status (Optional)</Label>
                <Select value={interactionForm.status_change || "nochange"} onValueChange={(v) => setInteractionForm({...interactionForm, status_change: v === "nochange" ? "" : v})}>
                  <SelectTrigger data-testid="interaction-status-select">
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nochange">No change</SelectItem>
                    {statuses.map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={interactionForm.notes}
                onChange={(e) => setInteractionForm({...interactionForm, notes: e.target.value})}
                placeholder="Describe the interaction..."
                required
                data-testid="interaction-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsInteractionDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-interaction-btn">Add Interaction</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Reminder Dialog */}
      <Dialog open={isReminderDialogOpen} onOpenChange={setIsReminderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Reminder</DialogTitle>
            <DialogDescription>Set a follow-up reminder</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddReminder} className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={reminderForm.title}
                onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})}
                placeholder="Follow-up call"
                required
                data-testid="reminder-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={reminderForm.description}
                onChange={(e) => setReminderForm({...reminderForm, description: e.target.value})}
                placeholder="Additional notes..."
                data-testid="reminder-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={reminderForm.due_date}
                onChange={(e) => setReminderForm({...reminderForm, due_date: e.target.value})}
                required
                data-testid="reminder-due-date"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsReminderDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-reminder-btn">Add Reminder</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Temporary Link Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={(open) => { setIsLinkDialogOpen(open); if (!open) { setGeneratedLinkUrl(''); setLinkForm({ target_branch_id: '', expires_hours: 24, allowed_ips: '', is_external: false }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Temporary Access Link</DialogTitle>
            <DialogDescription>Share this record with another branch or external user</DialogDescription>
          </DialogHeader>
          {generatedLinkUrl ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm font-medium text-green-800 mb-2">Link created successfully!</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={generatedLinkUrl}
                    readOnly
                    className="text-xs font-mono select-all"
                    data-testid="generated-link-input"
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => e.target.select()}
                  />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(generatedLinkUrl)} data-testid="copy-link-btn">
                    {copyFeedback || 'Copy'}
                  </Button>
                </div>
                {copyFeedback && copyFeedback !== 'Copied!' && (
                  <p className="text-xs text-amber-600 mt-1">{copyFeedback}</p>
                )}
                {linkForm.is_external && (
                  <p className="text-xs text-muted-foreground mt-2">External users will need to enter the policy number to access this link.</p>
                )}
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => { setIsLinkDialogOpen(false); setGeneratedLinkUrl(''); setLinkForm({ target_branch_id: '', expires_hours: 24, allowed_ips: '', is_external: false }); }}>Close</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreateTemporaryLink} className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-md">
                <input
                  type="checkbox"
                  id="is-external"
                  checked={linkForm.is_external}
                  onChange={(e) => setLinkForm({...linkForm, is_external: e.target.checked, target_branch_id: ''})}
                  className="h-4 w-4 rounded"
                  data-testid="external-link-toggle"
                />
                <Label htmlFor="is-external" className="text-sm cursor-pointer">
                  External link (no branch — policy number auth required)
                </Label>
              </div>
              {!linkForm.is_external && (
                <div className="space-y-2">
                  <Label>Target Branch</Label>
                  <Select value={linkForm.target_branch_id} onValueChange={(v) => setLinkForm({...linkForm, target_branch_id: v})}>
                    <SelectTrigger data-testid="target-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.filter(b => b.id !== record.branch_id && b.is_active).map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Expires In (Hours)</Label>
                <Input
                  type="number"
                  value={linkForm.expires_hours}
                  onChange={(e) => setLinkForm({...linkForm, expires_hours: e.target.value})}
                  min={1}
                  max={168}
                  data-testid="link-expires-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Allowed IPs (Optional, comma-separated)</Label>
                <Input
                  value={linkForm.allowed_ips}
                  onChange={(e) => setLinkForm({...linkForm, allowed_ips: e.target.value})}
                  placeholder="192.168.1.1, 10.0.0.1"
                  data-testid="link-allowed-ips"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsLinkDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={!linkForm.is_external && !linkForm.target_branch_id} data-testid="create-link-btn">Create Link</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={isEditEmailDialogOpen} onOpenChange={setIsEditEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Email</DialogTitle>
            <DialogDescription>This change will be audit logged</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateEmail} className="space-y-4">
            <div className="space-y-2">
              <Label>New Email</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                data-testid="new-email-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditEmailDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="update-email-btn">Update Email</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Document Dialog */}
      <Dialog open={isShareDocDialogOpen} onOpenChange={setIsShareDocDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Document</DialogTitle>
            <DialogDescription>Send document via email</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleShareDocument} className="space-y-4">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                required
                data-testid="share-email-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsShareDocDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="share-doc-btn">Share</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DocumentViewer document={viewingDoc} open={!!viewingDoc} onClose={() => setViewingDoc(null)} canDownload={user?.role === 'super_admin' || user?.role === 'branch_admin'} />
    </div>
  );
};

export default RecordDetailPage;
